import { reset } from 'redux-form';
import history from '../../../history';
import * as MsgBarActions from '../system-shared/message-bar-action-creator';
import * as api from './secure-message-api';
import * as actions from './actions/creators';
import { getLocalizedFacilityContent } from '../localization/localization-api';

const legalMessageLoaded = (legalMessage) =>
  actions.secureMessageLegalHeaderLoaded(legalMessage);

export const loadLegalMessage = (facilityId, languageCode) => (dispatch) =>
  getLocalizedFacilityContent('SecurityMessageLegal', facilityId, languageCode)
    .then(
      (response) => dispatch(legalMessageLoaded(response.data))
    );

export const loadMessageThreads = (
  patientId, language, timeZone, timeZoneOffset) => (dispatch) => (
  api.getThreads(patientId).then(
    (response) => {
      dispatch(actions.messageThreadsLoaded(
        response.data, language, timeZone, timeZoneOffset));
    },
    (axiosResponse) => {
      const error = axiosResponse.response;
      if (!error.isSessionTimeout) {
        dispatch(MsgBarActions.showErrorMessage('securemessagethreadsload'));
      }
    }
  )
);

export const getUnreadCount = (patientId) => (dispatch) =>
  api.getUnreadCount(patientId).then(
    (response) => {
      dispatch(actions.secureMessageUnreadCountLoaded(response.data));
    },
    (axiosResponse) => {
      const error = axiosResponse.response;
      if (!error.isSessionTimeout) {
        dispatch(MsgBarActions.showErrorMessage('securemessageunreadcountloadfailed'));
      }
    }
  );

export const patchReviewedThreadMessages = (
  patientId, threadId, messages, language, timeZone, timeZoneOffset) =>
  (dispatch, getState) => {
    const threads = getState().secureMessage.threads;
    const filteredThread = threads.find((t) => t.threadId === threadId);
    if (filteredThread) {
      const patientMember = filteredThread.members.find((m) => m.patientId === filteredThread.referencedPatientId);
      if (!patientMember.areMessagesReviewed) {
        const threadMemberId = patientMember.threadMemberId;
        return api.patchReviewedThreadMessages(patientId, threadId, threadMemberId, messages).then(
          () => {
            dispatch(loadMessageThreads(patientId, language, timeZone, timeZoneOffset));
            dispatch(getUnreadCount(patientId));
          },
          (axiosResponse) => {
            const error = axiosResponse.response;
            if (!error.isSessionTimeout) {
              dispatch(MsgBarActions.showErrorMessage('securemessagemarkreviewedfailed'));
            }
          }
        );
      }
    }
    return null;
  };

export const patchArchivedThread = (patientId, threadId, isArchived, language, timeZone, timeZoneOffset) =>
  (dispatch) => {
    const isArchivedBoolean = isArchived === 'Archived';
    return api.patchThreadMember(patientId, threadId, isArchivedBoolean).then(
      () => {
        dispatch(loadMessageThreads(patientId, language, timeZone, timeZoneOffset));
        dispatch(getUnreadCount(patientId));
        if (isArchivedBoolean) {
          dispatch(MsgBarActions.showSuccessMessage('securemessagethreadarchived'));
        } else {
          dispatch(MsgBarActions.showSuccessMessage('securemessagethreadunarchived'));
        }
      },
      (axiosResponse) => {
        const error = axiosResponse.response;
        if (!error.isSessionTimeout) {
          dispatch(MsgBarActions.showErrorMessage('securemessagearchivefailed'));
        }
      }
    );
  };

export const threadMessagesRequested = (
  patientId, threadId, language, timeZone, timeZoneOffset) =>
  (dispatch) => (
    api.getThreadMessages(patientId, threadId).then(
      (response) => {
        if (response.data.length === 0) {
          dispatch(MsgBarActions.showErrorMessage('securemessagenotavailable'));
        }
        const messages = response.data;
        dispatch(actions.threadMessagesLoaded(messages, language, timeZone, timeZoneOffset));
        return response;
      },
      (axiosResponse) => {
        const error = axiosResponse.response;
        if (!error.isSessionTimeout) {
          dispatch(MsgBarActions.showErrorMessage('securemessagenotavailable'));
        }
      }
    )
  );

export const sendSecureMessage = (params) => (dispatch) =>
  api.postSecureMessage(params).then(
    (result) => {
      dispatch(reset('securemessageform'));
      if (params.successRoute) {
        history.push(params.successRoute);
      }
      dispatch(MsgBarActions.showSuccessMessage('securemessagesent'));
      dispatch(loadMessageThreads(params.patientId, params.language, params.timeZone, params.timeZoneOffset));
      dispatch(threadMessagesRequested(
        params.patientId, result.data.threadId, params.language, params.timeZone, params.timeZoneOffset));

      if (!params.compact) {
        // NOTE: "result.data.threadId" is a number/int.
        // This value is stored in the selectedThreadIndex of the redux securemessage state.
        dispatch(actions.secureMessageThreadSelected(result.data.threadId));
      }
    },
    (axiosResponse) => {
      const error = axiosResponse.response;
      dispatch(reset('securemessageform'));
      if (!error.isSessionTimeout) {
        dispatch(MsgBarActions.showErrorMessage('securemessagesendfailed'));
      }
    }
  );

export const putSecureMessage = (patientId, threadId, archive, successMessage, language, timeZone, timeZoneOffset) =>
  (dispatch) =>
    api.putThread(patientId, threadId, archive).then(
      () => {
        dispatch(MsgBarActions.showSuccessMessage(successMessage));
        dispatch(loadMessageThreads(patientId, language, timeZone, timeZoneOffset));
      },
      (axiosResponse) => {
        const error = axiosResponse.response;
        if (!error.isSessionTimeout) {
          dispatch(MsgBarActions.showErrorMessage('securemessagearchivefailed'));
        }
      }
    );

export const toggleMessageThreadsFilter = (filter) => (dispatch) =>
  (dispatch(actions.messageFilterToggled(filter)));

export const replyToSecureMessage =
(patientId, threadId, replyText, languageCode, timeZone, timeZoneOffset) => (dispatch) =>
  api.postReplyToMessage(patientId, threadId, replyText).then(
    () => {
      dispatch(loadMessageThreads(patientId, languageCode, timeZone, timeZoneOffset));
      dispatch(threadMessagesRequested(patientId, threadId, languageCode, timeZone, timeZoneOffset));
      dispatch(MsgBarActions.showSuccessMessage('securemessagesent'));
    },
    (axiosResponse) => {
      const error = axiosResponse.response;
      if (!error.isSessionTimeout) {
        dispatch(MsgBarActions.showErrorMessage('securemessagesendfailed'));
      }
    }
  );
